import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../utils/apiClient';
import {
  X, Search, Loader2, FileSpreadsheet, Plus, Save, Trash2, Edit3,
  RefreshCcw, AlertCircle, CheckCircle2, Send, Database, History,
  Download, ClipboardList, Calendar, FilterX, Upload, Eye, FileText
} from 'lucide-react';

// ─── Modal de archivos para Control Equidad ──────────────────────
const FileViewerEquidad = ({ caso, onDeleted }) => {
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);

  const fetchFiles = React.useCallback(async () => {
    setLoadingFiles(true);
    try {
      const res = await api.get(`/expedientes/${caso}/files`);
      setFiles(res.archivos || []);
    } catch {
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [caso]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleEliminar = async (fileName) => {
    if (!window.confirm(`¿Eliminar el documento ${fileName}?`)) return;
    try {
      await api.delete(`/expedientes/${caso}/files/${fileName}`);
      fetchFiles();
      onDeleted();
    } catch {
      alert('Error al eliminar el archivo.');
    }
  };

  if (loadingFiles) return (
    <div className="flex flex-col items-center py-10 gap-3">
      <Loader2 size={24} className="animate-spin text-emerald-500" />
      <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Cargando archivos...</p>
    </div>
  );

  if (files.length === 0) return (
    <div className="flex flex-col items-center justify-center py-12 text-center px-4">
      <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center mb-3">
        <FileText size={22} className="text-slate-500" />
      </div>
      <p className="text-slate-400 font-medium text-sm">Sin documentos adjuntos</p>
      <p className="text-slate-600 text-xs mt-1">No hay archivos cargados para este caso</p>
    </div>
  );

  return (
    <div className="p-5 max-h-[420px] overflow-y-auto custom-scrollbar space-y-3">
      {files.map((file, idx) => (
        <div key={idx} className="p-3 rounded-2xl border border-slate-700/50 bg-slate-800/40 hover:bg-slate-800/70 transition-colors">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
              <FileText size={16} className="text-emerald-400" />
            </div>
            <span className="text-sm font-medium text-slate-200 truncate">{file}</span>
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-slate-700/50 pt-2.5">
            <a
              href={`https://app.legalpp.com.co/legalplus/expedientes/${caso}/files/${file}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <Eye size={13} /> Ver documento
            </a>
            <button
              onClick={() => handleEliminar(file)}
              className="flex items-center gap-1 px-2.5 py-1 bg-red-900/30 text-red-400 rounded-lg text-[10px] font-bold uppercase hover:bg-red-900/60 hover:text-red-300 transition-all active:scale-95"
            >
              <Trash2 size={11} /> Eliminar
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};


const SeguimientoEquidad = ({ isOpen, onClose, readOnly = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // 1. FILTROS AVANZADOS
  const [filters, setFilters] = useState({ placa: '', fechaDesde: '', fechaHasta: '' });

  // 2. ESTADOS DE MODALES
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showTraceModal, setShowTraceModal] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [traceNote, setTraceNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });

  // 3. LOS CAMPOS CON NOMBRES CORRECTOS (coinciden con columnas de BD)
  const INITIAL_FORM_STATE = {
    firma_asignada: 'LegalPlus Partners sas',
    nombre_abogado_firma: '',
    siniestro: '',
    caso: '',
    placa_asegurado: '',
    fecha_siniestro: '',
    asignador: '',
    fecha_asignacion_primera_vez: '',
    clase_reclamo: '',
    tipo_jurisdiccion: '',
    etapa_actual_proceso: '',
    nombre_victima: '',
    nombre_apoderado_victima: '',
    pretensiones: '',
    contingencia: '',
    respuesta_compania: '',
    valor_autorizado: '',
    contrapropuesta: '',
    gestion: '',
    fecha_actualizacion: new Date().toISOString().split('T')[0],
    observaciones: ''
  };

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  const fileInputEquidadRef = useRef(null);
  const selectedCasoRef = useRef(null);
  const [uploadingCaso, setUploadingCaso] = useState(null);
  const [viewingCaso, setViewingCaso] = useState(null);

  const handleUploadClick = (caso) => {
    selectedCasoRef.current = caso;
    fileInputEquidadRef.current.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const caso = selectedCasoRef.current;
    if (!files.length || !caso) return;
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    setUploadingCaso(caso);
    try {
      // N9: backend (#3 Daniel) responde subidos[] y rechazados[].
      const data = await api.post(
        `/expedientes/${caso}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      // N9-bis: backend devuelve 'archivos' (lista de filenames guardados).
      const rechazados = (data && Array.isArray(data.rechazados)) ? data.rechazados : [];
      const guardados = (data && Array.isArray(data.archivos)) ? data.archivos : null;
      const subidos = guardados ? guardados.length : Math.max(files.length - rechazados.length, 0);
      if (rechazados.length === 0) {
        showFeedback(`Subido: ${subidos} archivo(s).`);
      } else {
        const lineas = rechazados.map((r) => `${r.nombre || r.archivo || r.filename || 'archivo'}: ${r.motivo || r.reason || 'rechazado'}`).join(' | ');
        showFeedback(`Subidos: ${subidos} · Rechazados: ${rechazados.length} (${lineas})`, 'error');
      }
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error al subir el documento';
      showFeedback(msg, 'error');
    } finally {
      setUploadingCaso(null);
      selectedCasoRef.current = null;
      e.target.value = null;
    }
  };

  const showFeedback = (msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/casos-legales');
      setData(res || []);
    } catch (err) {
      showFeedback("Error de sincronización", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOpen) fetchData(); }, [isOpen]);

  // 4. LÓGICA DE FILTRADO
  const filteredData = useMemo(() => {
    return data.filter(item => {
      const matchesSearch = !searchTerm || Object.values(item).some(v =>
        String(v).toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesPlaca = !filters.placa || item.placa_asegurado?.toLowerCase().includes(filters.placa.toLowerCase());

      let matchesDate = true;
      if (item.fecha_siniestro && (filters.fechaDesde || filters.fechaHasta)) {
        const itemTime = new Date(item.fecha_siniestro).getTime();
        if (filters.fechaDesde && itemTime < new Date(filters.fechaDesde).getTime()) matchesDate = false;
        if (filters.fechaHasta) {
            const hastaTime = new Date(filters.fechaHasta).getTime() + 86400000;
            if (itemTime > hastaTime) matchesDate = false;
        }
      }
      return matchesSearch && matchesPlaca && matchesDate;
    });
  }, [data, searchTerm, filters]);

  // 5. FUNCIONES DE ACCIÓN
  const handleSaveCase = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (isEditing) {
        await api.put(`/casos-legales/${formData.id}`, formData);
        showFeedback("Registro actualizado");
      } else {
        await api.post('/casos-legales', formData);
        showFeedback("Caso creado");
      }
      setShowFormModal(false);
      fetchData();
    } catch (err) {
      showFeedback("Error en la operación", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este registro y su historial por completo?")) return;
    try {
      await api.delete(`/casos-legales/${id}`);
      showFeedback("Eliminado con éxito", "success");
      fetchData();
    } catch (err) {
      showFeedback("Error al eliminar", "error");
    }
  };

  const handleConfirmTrace = async () => {
    if (!traceNote.trim()) return;
    setActionLoading(true);
    try {
      const resDuplicar = await api.post(`/casos-legales/${selectedItem.id}/duplicar`);
      const newId = resDuplicar.id_nuevo || resDuplicar.id;

      await api.put(`/casos-legales/${newId}`, {
        gestion: traceNote,
        fecha_actualizacion: new Date().toISOString().split('T')[0]
      });
      showFeedback("Seguimiento guardado");
      setShowTraceModal(false);
      setTraceNote('');
      fetchData();
    } catch (err) {
      showFeedback("Fallo en trazabilidad", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const openHistory = (item) => {
    const history = data
      .filter(d => d.siniestro === item.siniestro)
      .sort((a, b) => new Date(b.fecha_actualizacion) - new Date(a.fecha_actualizacion));
    setHistoryData(history);
    setSelectedItem(item);
    setShowHistoryPanel(true);
  };

  const exportToExcel = async () => {
    try {
      const response = await api.post(
        '/casos-legales/exportar-equidad',
        filteredData,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Reporte_Equidad_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando Excel:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm transition-all font-sans">

      {toast.show && (
        <div className={`fixed bottom-6 right-6 z-[600] px-5 py-3 rounded-xl border text-[10px] font-black shadow-2xl animate-in fade-in slide-in-from-bottom-4
          ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border-rose-500/20 text-rose-400'}`}>
          {toast.msg.toUpperCase()}
        </div>
      )}

      <div className="bg-[#0f172a] border border-white/10 w-full max-w-[98vw] h-[95vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">

        {/* HEADER & FILTROS */}
        <div className="p-4 bg-slate-900/40 border-b border-white/5">
          <div className="flex justify-between items-center mb-4 text-white">
            <div className="flex items-center gap-3">
              <Database className="text-emerald-500" size={20} />
              <div>
                <h2 className="text-sm font-black uppercase tracking-widest">Equidad Seguros</h2>
                <p className="text-[9px] text-slate-500 font-bold uppercase">Control Integral de Procesos</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={exportToExcel} className="bg-slate-800 text-slate-300 px-3 py-1.5 rounded-lg text-[9px] font-black border border-white/5 flex items-center gap-2 tracking-widest uppercase hover:bg-slate-700 transition-all"><Download size={14}/> Exportar</button>
              {!readOnly && <button onClick={() => { setIsEditing(false); setFormData(INITIAL_FORM_STATE); setShowFormModal(true); }} className="bg-emerald-600 text-white px-4 py-1.5 rounded-lg font-black text-[9px] flex items-center gap-2 shadow-lg uppercase tracking-widest hover:bg-emerald-500 transition-all"><Plus size={14}/> Nuevo Caso</button>}
              <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-slate-500 hover:text-white ml-2 transition-all"><X size={18}/></button>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-3">
            <div className="relative col-span-2 group">
              <Search className="absolute left-3 top-2.5 text-slate-700 group-focus-within:text-blue-500 transition-colors" size={14} />
              <input type="text" placeholder="Búsqueda global..." className="w-full bg-black/20 border border-white/5 rounded-xl pl-9 pr-4 py-2 text-[11px] text-white outline-none focus:border-emerald-500/30 transition-all font-medium" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <input type="text" placeholder="PLACA..." className="bg-black/20 border border-white/5 rounded-xl px-4 py-2 text-[11px] text-white outline-none uppercase font-mono focus:border-emerald-500/30 transition-all" onChange={(e) => setFilters({...filters, placa: e.target.value})} />
            <input type="date" className="bg-black/20 border border-white/5 rounded-xl px-4 py-2 text-[11px] text-slate-400 outline-none" onChange={(e) => setFilters({...filters, fechaDesde: e.target.value})} />
            <input type="date" className="bg-black/20 border border-white/5 rounded-xl px-4 py-2 text-[11px] text-slate-400 outline-none" onChange={(e) => setFilters({...filters, fechaHasta: e.target.value})} />
          </div>
        </div>

        {/* TABLA PRINCIPAL */}
        <div className="flex-1 overflow-auto bg-[#0a0f1d] custom-scrollbar p-1">
          <table className="w-full text-left border-separate border-spacing-0 min-w-[3200px]">
            <thead className="sticky top-0 z-20 shadow-sm">
              <tr className="bg-slate-900/95 backdrop-blur-md">
                <th className="px-3 py-2 sticky left-0 bg-slate-900 z-30 text-center w-32 border-b border-white/5 shadow-xl font-black text-[9px] text-slate-500 uppercase tracking-widest">Acciones</th>
                {[
                  "Etapa Actual", "Siniestro", "Placa", "Abogado Firma", "Gestión Trazabilidad",
                  "Nombre Víctima", "Firma Asignada", "Caso", "Fecha Siniestro", "Asignador",
                  "Fecha Asignación", "Clase Reclamo", "Jurisdicción", "Apoderado",
                  "Pretensiones", "Contingencia", "Respuesta Cía", "Valor Autorizado",
                  "Contrapropuesta", "Observaciones", "Actualización"
                ].map(h => (
                  <th key={h} className="px-3 py-2 border-b border-white/5 font-black text-[9px] text-slate-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="text-[10px] text-slate-400">
              {filteredData.map((item) => (
                <tr key={item.id} className="hover:bg-emerald-500/[0.02] transition-colors border-b border-white/5">
                  <td className="px-2 py-1.5 sticky left-0 bg-[#0f172a] z-10 border-r border-white/5 text-center flex justify-center gap-1 shadow-xl">
                    {!readOnly && <button onClick={() => { setSelectedItem(item); setShowTraceModal(true); }} className="w-6 h-6 flex items-center justify-center text-blue-500 hover:bg-blue-500 hover:text-white rounded-md transition-all" title="Seguimiento (+)"><Plus size={13} strokeWidth={3}/></button>}
                    {!readOnly && <button onClick={() => { setFormData(item); setIsEditing(true); setShowFormModal(true); }} className="w-6 h-6 flex items-center justify-center text-amber-500 hover:bg-amber-600 hover:text-white rounded-md transition-all" title="Editar"><Edit3 size={12}/></button>}
                    {!readOnly && <button onClick={() => handleDelete(item.id)} className="w-6 h-6 flex items-center justify-center text-rose-500 hover:bg-rose-600 hover:text-white rounded-md transition-all" title="Eliminar"><Trash2 size={12}/></button>}
                    <button onClick={() => openHistory(item)} className="w-6 h-6 flex items-center justify-center text-slate-500 hover:bg-slate-700 hover:text-white rounded-md transition-all" title="Historial"><History size={12}/></button>
                    {(() => {
                      const docKey = item.caso || (item.placa_asegurado && item.fecha_siniestro ? `${item.placa_asegurado}_${item.fecha_siniestro}` : item.placa_asegurado);
                      return (<>
                        {!readOnly && <button onClick={() => handleUploadClick(docKey)} disabled={!docKey || (!!uploadingCaso && uploadingCaso === docKey)} className="w-6 h-6 flex items-center justify-center text-emerald-500 hover:bg-emerald-600 hover:text-white rounded-md transition-all disabled:opacity-40" title="Subir documento">{uploadingCaso && uploadingCaso === docKey ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}</button>}
                        <button onClick={() => setViewingCaso(docKey)} className="w-6 h-6 flex items-center justify-center text-amber-400 hover:bg-amber-600 hover:text-white rounded-md transition-all" title="Ver documentos"><Eye size={12}/></button>
                      </>);
                    })()}
                  </td>
                  <td className="px-3 py-1.5 max-w-[130px]"><div className="flex items-center gap-1.5 uppercase font-bold text-slate-300 truncate"><div className="shrink-0 h-1 w-1 rounded-full bg-emerald-500 shadow-[0_0_4px_#10b981]"></div><span className="truncate">{item.etapa_actual_proceso}</span></div></td>
                  <td className="px-3 py-1.5 font-mono font-bold text-emerald-500/70 whitespace-nowrap">{item.siniestro}</td>
                  <td className="px-3 py-1.5 font-mono font-bold text-blue-400 uppercase whitespace-nowrap">{item.placa_asegurado}</td>
                  <td className="px-3 py-1.5 font-bold text-slate-200 truncate max-w-[140px]">{item.nombre_abogado_firma}</td>
                  <td className="px-3 py-1.5 text-slate-300 italic bg-white/[0.02] font-medium max-w-[200px] truncate">{item.gestion}</td>
                  <td className="px-3 py-1.5 text-slate-300 truncate max-w-[140px]">{item.nombre_victima}</td>
                  <td className="px-3 py-1.5 opacity-50 truncate max-w-[120px]">{item.firma_asignada}</td>
                  <td className="px-3 py-1.5 truncate max-w-[100px]">{item.caso}</td>
                  <td className="px-3 py-1.5 text-slate-500 font-mono whitespace-nowrap">{item.fecha_siniestro}</td>
                  <td className="px-3 py-1.5 truncate max-w-[110px]">{item.asignador}</td>
                  <td className="px-3 py-1.5 font-mono whitespace-nowrap">{item.fecha_asignacion_primera_vez}</td>
                  <td className="px-3 py-1.5 truncate max-w-[110px]">{item.clase_reclamo}</td>
                  <td className="px-3 py-1.5 truncate max-w-[100px]">{item.tipo_jurisdiccion}</td>
                  <td className="px-3 py-1.5 truncate max-w-[130px]">{item.nombre_apoderado_victima}</td>
                  <td className="px-3 py-1.5 text-amber-500/80 font-bold whitespace-nowrap">{item.pretensiones}</td>
                  <td className="px-3 py-1.5 italic opacity-70 truncate max-w-[100px]">{item.contingencia}</td>
                  <td className="px-3 py-1.5 opacity-60 italic truncate max-w-[120px]">{item.respuesta_compania}</td>
                  <td className="px-3 py-1.5 text-emerald-400 font-bold whitespace-nowrap">{item.valor_autorizado}</td>
                  <td className="px-3 py-1.5 italic truncate max-w-[100px]">{item.contrapropuesta}</td>
                  <td className="px-3 py-1.5 opacity-60 truncate max-w-[120px]">{item.observaciones}</td>
                  <td className="px-3 py-1.5 font-mono text-[9px] text-slate-600 uppercase whitespace-nowrap">{item.fecha_actualizacion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PANEL DE HISTORIAL LATERAL */}
        {showHistoryPanel && (
          <div className="absolute inset-y-0 right-0 w-[420px] bg-slate-900 border-l border-white/10 shadow-2xl z-[400] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-white/5 flex justify-between items-center bg-slate-950/50 text-white">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-2"><History size={16} className="text-blue-500"/> Línea de Tiempo</h3>
              <button onClick={() => setShowHistoryPanel(false)} className="p-2 hover:bg-white/5 rounded-full transition-all"><X size={20}/></button>
            </div>
            <div className="p-6 overflow-auto space-y-6 flex-1 custom-scrollbar bg-slate-900/50 text-gray-300">
              {historyData.map((h) => (
                <div key={h.id} className="relative pl-6 border-l border-blue-500/20">
                  <div className="absolute -left-[5px] top-0 h-2 w-2 rounded-full bg-blue-600 shadow-lg"></div>
                  <p className="text-[9px] font-black text-slate-500 mb-2 uppercase tracking-tighter">{h.fecha_actualizacion} — {h.nombre_abogado_firma}</p>
                  <p className="text-[11px] text-slate-300 leading-relaxed bg-white/5 p-4 rounded-xl italic border border-white/5 shadow-inner font-medium leading-relaxed">{h.gestion}</p>
                </div>
              ))}
              {historyData.length === 0 && <p className="text-center text-[10px] text-slate-600 uppercase py-20 font-bold tracking-widest">No hay registros previos</p>}
            </div>
          </div>
        )}

        {/* MODAL FORMULARIO */}
        {showFormModal && (
          <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm">
            <form onSubmit={handleSaveCase} className="bg-slate-900 border border-white/10 w-full max-w-6xl max-h-[85vh] overflow-auto rounded-3xl shadow-2xl p-10 custom-scrollbar border-t-4 border-t-emerald-500 animate-in fade-in zoom-in duration-200">
              <div className="flex justify-between items-start mb-10 text-white">
                <div>
                  <h3 className="text-xl font-black uppercase tracking-[0.4em] mb-2">{isEditing ? 'EDITAR REGISTRO' : 'NUEVO EXPEDIENTE'}</h3>
                  <div className="h-1 w-24 bg-emerald-500 rounded-full shadow-[0_0_10px_#10b981]"></div>
                </div>
                <button type="button" onClick={() => setShowFormModal(false)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-slate-500 hover:text-white transition-all font-bold"><X size={20}/></button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-x-8 gap-y-6">
                {Object.keys(formData).filter(k => k !== 'id').map((key) => (
                  <div key={key} className={key === 'gestion' || key === 'observaciones' ? 'md:col-span-4' : ''}>
                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-2 ml-1 italic">{key.replace(/_/g, ' ')}</label>
                    {key === 'gestion' || key === 'observaciones' ? (
                      <textarea className="w-full bg-black/20 border border-white/5 rounded-xl p-4 text-[12px] text-white h-32 outline-none focus:border-emerald-500/30 transition-all shadow-inner" value={formData[key] || ''} onChange={(e) => setFormData({...formData, [key]: e.target.value})} />
                    ) : (
                      <input type={key.includes('fecha') ? 'date' : 'text'} value={formData[key] || ''} className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-2.5 text-[12px] text-white outline-none focus:border-emerald-500/30 font-medium shadow-inner" onChange={(e) => setFormData({...formData, [key]: e.target.value})} />
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-12 flex justify-end gap-4 border-t border-white/5 pt-6">
                <button type="button" onClick={() => setShowFormModal(false)} className="px-8 py-3 text-slate-500 font-bold text-[10px] uppercase tracking-[0.2em] hover:text-white transition-all">Descartar</button>
                <button type="submit" disabled={actionLoading} className="px-12 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl flex items-center gap-2 active:scale-95 transition-all">
                  {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={16} />} Finalizar Registro
                </button>
              </div>
            </form>
          </div>
        )}

        <input type="file" ref={fileInputEquidadRef} className="hidden" onChange={handleFileChange} />

        {/* MODAL VISUALIZACIÓN DOCUMENTOS */}
        {viewingCaso && (
          <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in duration-200">
              <div className="px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-800/60 to-slate-900 flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-white text-sm">Documentación</h3>
                  <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest mt-0.5">{viewingCaso}</p>
                </div>
                <button onClick={() => setViewingCaso(null)} className="p-1.5 hover:bg-slate-700 rounded-xl transition-colors active:scale-95">
                  <X size={18} className="text-slate-400" />
                </button>
              </div>
              <FileViewerEquidad caso={viewingCaso} onClose={() => setViewingCaso(null)} onDeleted={() => {}} />
            </div>
          </div>
        )}

        {/* MODAL TRAZABILIDAD */}
        {showTraceModal && (
          <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
            <div className="bg-slate-900 border border-white/10 w-full max-w-md rounded-[2rem] p-8 shadow-2xl animate-in zoom-in duration-200 border-t-4 border-t-blue-600">
              <div className="flex items-center gap-3 mb-6 text-blue-400">
                <ClipboardList size={24} />
                <h3 className="text-sm font-black uppercase tracking-[0.2em]">Registrar Seguimiento</h3>
              </div>
              <textarea autoFocus className="w-full bg-black/20 border border-white/5 rounded-2xl p-5 text-[12px] text-slate-200 h-40 outline-none focus:border-blue-500/40 shadow-inner leading-relaxed" placeholder="REGISTRE LA NUEVA GESTIÓN O AVANCE..." value={traceNote} onChange={(e) => setTraceNote(e.target.value)} />
              <div className="mt-6 flex gap-3">
                <button onClick={() => setShowTraceModal(false)} className="flex-1 py-3 text-slate-600 font-black text-[10px] uppercase tracking-widest text-center">Cerrar</button>
                <button onClick={handleConfirmTrace} disabled={actionLoading || !traceNote.trim()} className="flex-[2] py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2">
                  {actionLoading ? <Loader2 size={14} className="animate-spin" /> : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeguimientoEquidad;
